// ---------------------------------------------------------------------------
// RichText Renderer (editor key: RichText)
// ---------------------------------------------------------------------------
// Renders a long-form text block from the editor's lightweight markdown-like
// syntax. Recognised constructs:
//   * Lines starting with `## ` become `<h2>` headings.
//   * Lines starting with `### ` become `<h3>` headings.
//   * Inline `**bold**` and `*italic*` are wrapped in `<strong>` / `<em>`.
//   * All other non-blank lines become `<p>` paragraphs.
// Every text fragment passes through the `text()` escaper so untrusted input
// cannot inject markup. Font size is constrained to a small safelist that
// drives a class hook rather than inline styles. No JavaScript, no DOM
// dependencies — pure SSR markup.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

type FontSizeKey = "sm" | "md" | "lg";

interface RichTextProps {
  content?: string;
  maxWidth?: string;
  fontSize?: FontSizeKey | string;
}

const VALID_FONT_SIZES: ReadonlySet<FontSizeKey> = new Set(["sm", "md", "lg"]);
const MAX_WIDTH_PATTERN = /^[0-9]{1,4}(px|rem|em|%|ch)$/;

function resolveFontSize(value: unknown): FontSizeKey {
  if (typeof value === "string" && VALID_FONT_SIZES.has(value as FontSizeKey)) {
    return value as FontSizeKey;
  }
  return "md";
}

function resolveMaxWidth(value: unknown): string {
  if (typeof value === "string" && MAX_WIDTH_PATTERN.test(value.trim())) {
    return value.trim();
  }
  return "720px";
}

// Render inline `**bold**` and `*italic*` while escaping every other slice.
// Tokens are parsed left-to-right so unbalanced markers degrade to text.
function renderInline(input: string): string {
  const parts: string[] = [];
  let cursor = 0;

  while (cursor < input.length) {
    const boldStart = input.indexOf("**", cursor);
    const italicStart = input.indexOf("*", cursor);

    let nextIndex = -1;
    let isBold = false;
    if (boldStart !== -1 && (italicStart === -1 || boldStart <= italicStart)) {
      nextIndex = boldStart;
      isBold = true;
    } else if (italicStart !== -1) {
      nextIndex = italicStart;
      isBold = false;
    }

    if (nextIndex === -1) {
      parts.push(text(input.slice(cursor)));
      break;
    }

    if (nextIndex > cursor) {
      parts.push(text(input.slice(cursor, nextIndex)));
    }

    const markerLength = isBold ? 2 : 1;
    const closeMarker = isBold ? "**" : "*";
    const contentStart = nextIndex + markerLength;
    const closeIndex = input.indexOf(closeMarker, contentStart);
    if (closeIndex === -1) {
      parts.push(text(input.slice(nextIndex)));
      break;
    }
    const inner = input.slice(contentStart, closeIndex);
    const tag = isBold ? "strong" : "em";
    parts.push(`<${tag}>${text(inner)}</${tag}>`);
    cursor = closeIndex + markerLength;
  }

  return parts.join("");
}

function renderLine(line: string): string {
  if (line.startsWith("## ")) {
    return `  <h2 class="ease-rich-text__h2">${renderInline(line.slice(3))}</h2>`;
  }
  if (line.startsWith("### ")) {
    return `  <h3 class="ease-rich-text__h3">${renderInline(line.slice(4))}</h3>`;
  }
  return `  <p class="ease-rich-text__p">${renderInline(line)}</p>`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as RichTextProps;
  const raw = typeof p.content === "string" ? p.content : "";
  const lines = raw
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length === 0) {
    return "<!-- rich-text missing content -->";
  }

  const fontSize = resolveFontSize(p.fontSize);
  const maxWidth = resolveMaxWidth(p.maxWidth);
  const body = lines.map(renderLine).join("\n");

  return `<section class="ease-section ease-rich-text ease-rich-text--size-${attr(fontSize)}" style="max-width: ${attr(maxWidth)};">
${body}
</section>`;
}

registerRenderer("rich-text", { render });
